import React, { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { ArrowLeft, RefreshCw, LogOut, Sparkles, Plus, CreditCard, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ProfileProvider, useProfile } from './ProfileContext';
import Logo from '../Logo';
import WelcomeBanner from '../WelcomeBanner';
import UpgradeBanner, { PremiumBadge, ManageSubscriptionButton } from '../UpgradeBanner';

// Components
import DependencyPrompt from './components/DependencyPrompt';
import WaterfallSummary from './components/WaterfallSummary';

// Sections
import ISPSection from './sections/ISPSection';
import CellularSection from './sections/CellularSection';
import CreditCardsSection from './sections/CreditCardsSection';
import MembershipsSection from './sections/MembershipsSection';
import BundlesSection from './sections/BundlesSection';
import SubscriptionsSection from './sections/SubscriptionsSection';
import EligibilitySection from './sections/EligibilitySection';

const SmartProfileEditorContent = ({ onBack }) => {
  const { user, signOut } = useAuth();
  const [showOptimizePanel, setShowOptimizePanel] = useState(false);
  const [showOptimizeTooltip, setShowOptimizeTooltip] = useState(false);
  
  const {
    loading,
    error,
    profile,
    pendingChanges,
    subscriptions,
    creditCards,
    bundles,
    allocations,
    loadProfile,
    loadReferenceData,
    confirmPendingChanges,
    cancelPendingChanges,
    toggleSection,
  } = useProfile();

  useEffect(() => {
    loadProfile();
    loadReferenceData();
  }, [loadProfile, loadReferenceData]);

  // Calculate unused credits for optimize button badge
  const totalCreditsAvailable = creditCards.reduce((sum, c) => 
    sum + (parseFloat(c.streaming_credit_monthly) || 0), 0
  );
  
  // Use allocations table as the source of truth for credits used
  const allocationsTotal = (allocations || []).reduce((sum, a) => 
    sum + (parseFloat(a.amount_allocated) || 0), 0
  );
  
  // Fallback: also check credit_applied on subscriptions/bundles
  const subscriptionCreditsUsed = subscriptions.reduce((sum, s) => 
    sum + (parseFloat(s.credit_applied) || 0), 0
  );
  const bundleCreditsUsed = (bundles || []).reduce((sum, b) => 
    sum + (parseFloat(b.credit_applied) || 0), 0
  );
  
  // Use the higher of: allocations table OR direct credit_applied fields
  const totalCreditsUsed = Math.max(allocationsTotal, subscriptionCreditsUsed + bundleCreditsUsed);
  const unusedCredits = Math.max(0, totalCreditsAvailable - totalCreditsUsed);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-purple-200">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <p className="text-red-600 mb-4">Failed to load profile: {error}</p>
          <button
            onClick={loadProfile}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 pb-8">
      <Toaster position="top-right" />
      
      {/* Dependency Confirmation Modal */}
      {pendingChanges && (
        <DependencyPrompt
          pendingChanges={pendingChanges}
          onConfirm={confirmPendingChanges}
          onCancel={cancelPendingChanges}
        />
      )}

      {/* Header */}
      <div className="sticky top-0 z-40 bg-gradient-to-r from-purple-900/95 to-indigo-900/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-purple-200 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to Chat</span>
          </button>

          <Logo size="small" variant="glow" />

          <button
            onClick={signOut}
            className="flex items-center gap-2 text-purple-200 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="hidden sm:inline">Log Out</span>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pt-6">
        {/* Welcome Banner */}
        <div className="mb-6">
          <WelcomeBanner variant="gradient" />
        </div>

        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-1">Profile & Settings</h1>
          <p className="text-purple-200 text-sm">
            Manage your streaming services and maximize your savings
          </p>
        </div>

        {/* Main Layout: Summary Left (7 cols), Edit Sections Right (5 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* ===== LEFT COLUMN: Summary & Services ===== */}
          <div className="lg:col-span-7 space-y-4">
            
            {/* Waterfall Summary */}
            <WaterfallSummary onOptimizeClick={() => setShowOptimizePanel(true)} />

            {/* Optimize Button - Hide when panel is open */}
            {!showOptimizePanel && (
              <div className="relative">
                <button
                  onClick={() => setShowOptimizePanel(true)}
                  onMouseEnter={() => setShowOptimizeTooltip(true)}
                  onMouseLeave={() => setShowOptimizeTooltip(false)}
                  className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold text-lg transition-all bg-gradient-to-r from-yellow-400 to-orange-400 text-yellow-900 hover:from-yellow-300 hover:to-orange-300 shadow-lg shadow-orange-500/20"
                >
                  <Sparkles className="w-6 h-6" />
                  <span>Optimize My Savings</span>
                  {unusedCredits > 0 && (
                    <span className="px-2 py-1 bg-yellow-900/20 rounded-full text-sm">
                      ${unusedCredits.toFixed(0)} unused!
                    </span>
                  )}
                </button>
                
                {/* Tooltip */}
                {showOptimizeTooltip && (
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-xl z-10 w-72 text-center">
                    <p>Get AI-powered recommendations to reduce your costs, or explore what-if scenarios manually.</p>
                    <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 border-8 border-transparent border-b-gray-900"></div>
                  </div>
                )}
              </div>
            )}

            {/* Optimize Panel */}
            {showOptimizePanel && (
              <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-400/30 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                    Optimization Suggestions
                  </h3>
                  <button 
                    onClick={() => setShowOptimizePanel(false)}
                    className="text-white/60 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                {unusedCredits > 0 ? (
                  <div className="space-y-3 mb-4">
                    <div className="bg-white/10 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-medium text-white">You have ${unusedCredits.toFixed(2)} in unused credits</p>
                          <p className="text-sm text-purple-200 mt-1">
                            Allocate your card credits to services to save money each month.
                          </p>
                        </div>
                        <button 
                          onClick={() => {
                            setShowOptimizePanel(false);
                            // Open the Credit Cards section
                            toggleSection('creditCards');
                            // Scroll to Credit Cards section
                            setTimeout(() => {
                              document.getElementById('credit-cards-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 100);
                          }}
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors flex-shrink-0"
                        >
                          Allocate
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white/10 rounded-lg p-4 mb-4">
                    <p className="text-green-400 font-medium">✓ All credits are allocated!</p>
                    <p className="text-sm text-purple-200 mt-1">
                      Your setup looks optimized. Check back if you add new cards or services.
                    </p>
                  </div>
                )}
                
                {/* Manual What-If */}
                <div className="border-t border-white/20 pt-4">
                  <p className="text-sm text-purple-200 mb-3">Explore scenarios:</p>
                  <div className="flex flex-wrap gap-2">
                    <button className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors">
                      What if I add a card?
                    </button>
                    <button className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors">
                      What if I change ISP?
                    </button>
                    <button className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors">
                      What if I cancel a service?
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Upgrade Card for Free Users - Prominent placement in left column */}
            {profile.planType !== 'premium' && (
              <UpgradeBanner variant="card" />
            )}
          </div>
          
          {/* ===== RIGHT COLUMN: Edit Sections ===== */}
          <div className="lg:col-span-5 space-y-3">
            
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                <button className="flex items-center justify-center gap-2 px-3 py-2.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-medium transition-colors">
                  <Plus className="w-4 h-4" />
                  Add Service
                </button>
                <button className="flex items-center justify-center gap-2 px-3 py-2.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm font-medium transition-colors">
                  <CreditCard className="w-4 h-4" />
                  Add Card
                </button>
              </div>
            </div>
            
            {/* Collapsible Sections */}
            <BundlesSection />
            <SubscriptionsSection />
            <div id="credit-cards-section">
              <CreditCardsSection />
            </div>
            <MembershipsSection />
            <ISPSection />
            <CellularSection />
            <EligibilitySection />
            
            {/* Account Info - Compact */}
            <div className="bg-white/90 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-900">{user?.email}</span>
                  <span className="text-gray-400">•</span>
                  {profile.planType === 'premium' ? (
                    <PremiumBadge />
                  ) : (
                    <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                      Free
                    </span>
                  )}
                </div>
                {profile.planType === 'premium' ? (
                  <ManageSubscriptionButton />
                ) : (
                  <UpgradeBanner variant="inline" className="scale-90" />
                )}
              </div>
              {profile.planType === 'free' && (
                <p className="text-xs text-gray-500 mt-2">
                  {profile.queriesUsedMonth || 0} / 5 queries used this month
                  {(profile.queriesUsedMonth || 0) >= 5 && (
                    <span className="ml-1 text-red-500 font-medium">• Limit reached</span>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper with Provider
const SmartProfileEditor = ({ onBack }) => {
  const { user } = useAuth();

  return (
    <ProfileProvider userId={user?.id}>
      <SmartProfileEditorContent onBack={onBack} />
    </ProfileProvider>
  );
};

export default SmartProfileEditor;